/**
 * Formatters de data — o único lugar onde `Intl` de data pode aparecer
 * (docs/06-helpers-e-utils.md; `toLocaleDateString` inline é proibido).
 *
 * O formatter é criado uma vez no módulo: instanciar `Intl.DateTimeFormat` por
 * chamada custa caro em lista longa.
 */

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `21/07/2026 18:30` — data e hora curtas, para cartões e listas. */
export function formatDateTime(date: Date): string {
  return dateTimeFormatter.format(date);
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * `28/08/2026` — só a data, sem hora. Usada na linha "Conteúdo atualizado em
 * …" das páginas de contatos e links, que agora leem `updated_at` do banco em
 * vez de uma data escrita à mão no código: a hora do dia não importa para
 * quem só quer saber se a informação é recente.
 */
export function formatDate(date: Date): string {
  return dateFormatter.format(date);
}

/**
 * Meses abreviados, sem o ponto que o `Intl` do pt-BR põe ("set.") e sem o
 * "de" ("12 de set."): numa linha de cartão, "12 set" é o que se fala e o que
 * cabe. Fixos aqui, e não do `Intl`, para não variar entre motores.
 */
const SHORT_MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/** A partir de quantos dias a idade vira data: uma semana já não é "recente". */
const RELATIVE_DAYS_LIMIT = 7;

/**
 * "há 3 min", "há 2 h", "há 4 dias" — quanto tempo desde `date`, para filas,
 * avisos e notificações. A partir de 7 dias vira data curta: "12 set" (e
 * "12 set 2025" quando o ano não é o de `now`).
 *
 * A urgência é a informação: num pedido de resgate, "há 2 min" e "há 3 h" pedem
 * reações diferentes, e a data absoluta obriga a pessoa a fazer a conta. Abaixo
 * de um minuto é "agora mesmo" — o segundo exato não muda nenhuma decisão e só
 * faria o cartão piscar a cada tique.
 *
 * ## Por que dias por extenso, e por que a data depois de uma semana
 *
 * Era "há 95 d" (passada de UX, 2026-10): uma abreviação que ninguém fala e que,
 * em dado velho, esconde a idade real — "95 d" lê como número, "12 jul" lê como
 * "faz tempo". Num app de crise, alerta de três meses lido como vigente é
 * informação errada.
 *
 * `now` é injetável para o teste não depender do relógio; futuro (por clock
 * skew do aparelho) cai em "agora mesmo" em vez de "há -1 min".
 */
export function formatRelativeTime(date: Date, now: Date = new Date()): string {
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'agora mesmo';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days < RELATIVE_DAYS_LIMIT) return days === 1 ? 'há 1 dia' : `há ${days} dias`;

  const dayAndMonth = `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`;

  return date.getFullYear() === now.getFullYear()
    ? dayAndMonth
    : `${dayAndMonth} ${date.getFullYear()}`;
}

/** `yyyy-MM-ddTHH:mm` — o formato que `<input type="datetime-local">` entrega. */
const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * O momento é válido, em hora **local**.
 *
 * ⚠️ **`new Date(value)` sozinho não valida.** O V8 cai no parser legado quando
 * a string não passa no ISO, e ele **transborda**: `2026-02-30T10:00` vira 2 de
 * março, sem `NaN` nenhum. O único jeito de recusar é reconstruir a data e
 * conferir se ela devolve os mesmos números que entraram.
 *
 * Vivia como cópia local em `modules/reunion/schemas/missing-person.schema.ts`;
 * o formulário do pin da plataforma precisa da mesma validação, e uma segunda
 * cópia é o que docs/06 proíbe.
 */
export function isLocalDateTime(value: string): boolean {
  const match = LOCAL_DATE_TIME_PATTERN.exec(value);

  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  const parsed = new Date(year, month - 1, day, hour, minute);

  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day &&
    parsed.getHours() === hour &&
    parsed.getMinutes() === minute
  );
}

/**
 * `Date` → `yyyy-MM-ddTHH:mm`, em hora local — o inverso de `parseLocalDateTime`,
 * para o formulário reabrir já preenchido com o valor salvo.
 */
export function toLocalDateTimeInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * `yyyy-MM-ddTHH:mm` → `Date`, em hora local. Só chame depois de
 * `isLocalDateTime` confirmar o formato — aqui não há validação, para não
 * repeti-la a cada chamada.
 */
export function parseLocalDateTime(value: string): Date {
  const match = LOCAL_DATE_TIME_PATTERN.exec(value);

  if (!match) throw new Error(`Data e hora local inválidas: ${value}`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  return new Date(year, month - 1, day, hour, minute);
}

/** `dd/MM/yyyy` — o formato que a máscara de data (`maskDate`) entrega. */
const BRAZILIAN_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** `yyyy-MM-dd` — o formato de coluna `date` do Postgres. */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `31/12/2000` → `2000-12-31`, ou `null` se a data não existe.
 *
 * Mesma reconstrução de `isLocalDateTime`, pelo mesmo motivo: `31/02/2000`
 * casa com o formato e transbordaria em silêncio para março.
 */
export function parseBrazilianDate(value: string): string | null {
  const match = BRAZILIAN_DATE_PATTERN.exec(value.trim());

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  const exists =
    parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;

  return exists ? `${match[3]}-${match[2]}-${match[1]}` : null;
}

/** `2000-12-31` → `31/12/2000`, para o campo reabrir preenchido. Formato inválido vira `''`. */
export function formatIsoDateAsBrazilian(value: string): string {
  const match = ISO_DATE_PATTERN.exec(value);

  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/**
 * Idade completa em `today`, a partir de `yyyy-MM-dd`.
 *
 * Conta em calendário, não em milissegundos: quem nasceu em 31/10/2008 faz 18
 * exatamente em 31/10/2026, e a divisão por 365,25 erraria o dia do aniversário
 * — que é justamente o dia em que a resposta muda.
 */
export function ageOn(birthDate: string, today: Date = new Date()): number {
  const match = ISO_DATE_PATTERN.exec(birthDate);

  if (!match) throw new Error(`Data inválida: ${birthDate}`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const hadBirthdayThisYear =
    today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);

  return today.getFullYear() - year - (hadBirthdayThisYear ? 0 : 1);
}
